/**
 * Wire types for the saas-auth surfaces Brand Center calls. Sourced from
 * `docs/handoff/brand-center-fe-genesis-handoff.md` (§3–§5, auth) and
 * `docs/handoff/saas-brand-api-contract-handoff.md` (§2–§5, brand + email templates).
 *
 * Optional/nullable fields mirror the backend's `@JsonInclude(NON_NULL)` — read them
 * defensively; the two `/api/auth/me` shapes in particular MUST both parse without
 * crashing (genesis handoff §3.4).
 */

/** Standard success envelope. `/api/auth/refresh` and the exchange are FLAT (no envelope). */
export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

/* --------------------------------------------------------------- enums as unions */

export const TENANT_USER_ROLES = ['OWNER', 'SUPER_ADMIN', 'ADMIN', 'USER'] as const;
export type TenantUserRole = (typeof TENANT_USER_ROLES)[number];

export const PLATFORM_ADMIN_ROLES = ['SAAS_ADMIN', 'SUPER_ADMIN'] as const;
export type PlatformAdminRole = (typeof PLATFORM_ADMIN_ROLES)[number];

export const TENANT_STATUSES = ['ACTIVE', 'INACTIVE', 'SUSPENDED'] as const;
export type TenantStatus = (typeof TENANT_STATUSES)[number];

/** FE-static (api-contract handoff §5). Login-page layouts saas_login renders. */
export const BRAND_TEMPLATES = ['centered', 'split-hero', 'backdrop'] as const;
export type BrandTemplate = (typeof BRAND_TEMPLATES)[number];

/** FE-static closed set (api-contract handoff §5). */
export const BRAND_AUTH_PROVIDERS = ['password', 'google', 'microsoft'] as const;
export type BrandAuthProvider = (typeof BRAND_AUTH_PROVIDERS)[number];

/**
 * The 6 fixed email-template slots — FE-static (api-contract handoff §4). Note this is
 * NOT saas-admin's 5-slot list: Brand Center adds `GUEST_VERIFICATION`.
 */
export const EMAIL_TEMPLATE_NAMES = [
  'EMAIL_VERIFICATION',
  'PASSWORD_RESET',
  'GOOGLE_SIGNIN_NOTICE',
  'MFA_CODE',
  'GUEST_VERIFICATION',
  'TENANT_INVITATION',
] as const;
export type EmailTemplateName = (typeof EMAIL_TEMPLATE_NAMES)[number];

/* -------------------------------------------------------------------- auth DTOs */

export interface AuthUser {
  id: number;
  email: string;
  name?: string | null;
  locale?: string | null;
  emailVerified?: boolean;
  googleLinked?: boolean;
}

export interface AuthTenant {
  id: number;
  slug: string;
  name: string;
  status?: TenantStatus;
}

/**
 * The token-minting response body (genesis handoff §5) — returned FLAT by
 * `POST /api/auth/refresh` and `POST /api/auth/session/exchange`.
 *
 * `refreshToken` is legacy redundancy and MUST be ignored: the HttpOnly
 * `journix_refresh*` cookie is authoritative. `csrfToken` is the authoritative source
 * for store-token CSRF — the `journix_csrf` cookie is cross-origin-unreadable here.
 * Expiry arrives as `expiresIn` (seconds); `expiresAt` (epoch ms) is the canonical
 * factory's field name and is accepted too.
 */
export interface AuthTokenResponse {
  accessToken: string;
  tokenType?: string;
  expiresIn?: number | null;
  expiresAt?: number | null;
  /** Legacy URL/body redundancy — never stored. */
  refreshToken?: string | null;
  csrfToken?: string | null;
  user?: AuthUser;
  tenant?: AuthTenant;
  roles?: TenantUserRole[];
}

/** `GET /api/auth/me` for a tenant principal (genesis handoff §3.4). */
export interface AuthMeResponse {
  user: AuthUser;
  tenant: AuthTenant;
  roles: TenantUserRole[];
}

/**
 * `GET /api/auth/me` for a platform SUPER_ADMIN — a tenant-LESS principal, so there is
 * no `tenant` field. Detected by `principalType: 'PLATFORM'`.
 */
export interface OperatorMeResponse {
  userId: number;
  email: string;
  name?: string | null;
  principalType: 'PLATFORM';
  platformAdminRole: PlatformAdminRole;
}

/** Either shape can come back from `/api/auth/me`; narrow with `isOperatorMe`. */
export type MeResponse = AuthMeResponse | OperatorMeResponse;

/* ------------------------------------------------------------------- brand DTOs */

/**
 * The tenant's brand as read from `/api/tenant/brand**` (api-contract handoff §2).
 * `ownerTenantId` present ⇒ the brand is owned by the caller's tenant; null/absent ⇒
 * inherited (shared/product-default) and not yet forked.
 */
export interface BrandDto {
  id: number;
  productId: number;
  ownerTenantId?: number | null;
  slug: string;
  displayName: string;
  // Assets — raw editable keys + composed read-only preview URLs
  logoImage?: string | null;
  logoBaseUrl?: string | null;
  logoUrl?: string | null;
  faviconImage?: string | null;
  faviconBaseUrl?: string | null;
  faviconUrl?: string | null;
  heroImage?: string | null;
  heroBaseUrl?: string | null;
  heroImageUrl?: string | null;
  globalAssetBaseUrl?: string | null;
  // Style (free-form maps; conventional keys are an FE contract, not a server schema)
  colors: Record<string, unknown>;
  fonts: Record<string, unknown>;
  template?: BrandTemplate | null;
  // Copy — flattened out of `copyStrings`, which is also present raw
  copyStrings: Record<string, unknown>;
  shortName?: string | null;
  tagline?: string | null;
  heroCopy?: string | null;
  location?: string | null;
  // Flags
  authProviders: string[];
  signupEnabled: boolean;
  passwordResetEnabled: boolean;
  guestSignupEnabled?: boolean;
  primaryColorHover?: string | null;
  createdAt: string;
  updatedAt?: string;
}

/**
 * `PUT /api/tenant/brand` body (api-contract handoff §3). **FULL REPLACE** — omitted
 * fields are cleared, so always read → mutate → send the complete object. Excludes
 * `id`/`productId`/`ownerTenantId`, the composed URLs and the timestamps.
 */
export interface UpdateBrandRequest {
  slug: string;
  displayName: string;
  logoImage?: string | null;
  logoBaseUrl?: string | null;
  faviconImage?: string | null;
  faviconBaseUrl?: string | null;
  heroImage?: string | null;
  heroBaseUrl?: string | null;
  colors?: Record<string, unknown>;
  fonts?: Record<string, unknown>;
  copyStrings?: Record<string, unknown>;
  authProviders?: string[];
  signupEnabled?: boolean;
  passwordResetEnabled?: boolean;
  guestSignupEnabled?: boolean;
  template?: BrandTemplate | null;
  shortName?: string | null;
  tagline?: string | null;
  heroCopy?: string | null;
  location?: string | null;
  primaryColorHover?: string | null;
}

/* ----------------------------------------------------------- email-template DTOs */

export const EMAIL_LOCALES = ['en', 'es', 'fr', 'pt'] as const;
export type EmailLocale = (typeof EMAIL_LOCALES)[number];

export type EmailTemplateStatus = 'DEFAULT' | 'DRAFT' | 'PUBLISHED';
export type EmailTokenTarget = 'SUBJECT' | 'BODY' | 'BOTH';
export type EmailEffectiveSource = 'PLATFORM_DEFAULT' | 'TENANT_PUBLISHED';

export interface EmailTemplateCell {
  templateName: EmailTemplateName;
  locale: EmailLocale;
  status: EmailTemplateStatus;
  hasDraft: boolean;
  hasPublished: boolean;
  updatedAt: string | null;
}

export interface EmailTemplateContent {
  subject: string;
  bodyHtml: string;
  bodyText?: string | null;
}

export interface EmailEffectiveContent extends EmailTemplateContent {
  source: EmailEffectiveSource;
}

export interface EmailDraftContent extends EmailTemplateContent {
  tokensUsed: string[];
  versionNo: number | null;
  updatedAt: string | null;
  sanitized: boolean;
}

export interface EmailPublishedContent extends EmailTemplateContent {
  tokensUsed: string[];
  versionNo: number;
  updatedAt: string | null;
}

export interface EmailTemplateDetail {
  templateName: EmailTemplateName;
  locale: EmailLocale;
  status: EmailTemplateStatus;
  effective: EmailEffectiveContent;
  draft: EmailDraftContent | null;
  published: EmailPublishedContent | null;
}

export interface EmailTemplateDraftBody {
  subject: string;
  bodyHtml: string;
  bodyText?: string;
}

export interface EmailTokenDef {
  key: string;
  description: string;
  example: string;
  appliesTo: EmailTokenTarget;
}

export interface EmailSlotCatalog {
  templateName: EmailTemplateName;
  tokens: EmailTokenDef[];
}

export interface EmailPublishResult {
  templateName: EmailTemplateName;
  locale: EmailLocale;
  status: 'PUBLISHED';
  versionNo: number;
}

export interface EmailRevertResult {
  templateName: EmailTemplateName;
  locale: EmailLocale;
  status: EmailTemplateStatus;
}

export interface EmailResetResult {
  templateName: EmailTemplateName;
  locale: EmailLocale;
  status: 'DEFAULT';
}

export interface EmailServerPreview {
  subject: string;
  html: string;
}

export interface EmailTestSendResult {
  sentTo: string;
  status: 'SENT';
}
